import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  compoundFactor,
  getCalendarPolicyYear,
  getDailyBenefitAmountForEnvironment,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import { deriveIneligibilityReason } from '../deriveIneligibilityReason';

// ---------------------------------------------------------------------------
// NYL Asset Flex — reimbursement hybrid life with LTC rider.
//
// Two pools (Acceleration drawn first, then EOB), per-pool ACIO compound
// inflation at policy anniversary, monthly cap inflated by the same ACIO,
// flat Face Amount death benefit (depleted dollar-for-dollar by Acceleration
// drawdown, floored at Residual DB). No retroactive WP lump.
//
// Asset Flex's full story lives in the universal pools / waitingPeriod /
// facts / deltas — there's nothing per-claim or per-month that the universal
// shape doesn't already capture, so both state and deltas slots are empty.
// ---------------------------------------------------------------------------

export type NylAssetFlexState = Record<string, never>;
export type NylAssetFlexDeltas = Record<string, never>;

// Asset Flex at-issue pool sizing. Prefer the duration-based model
// (baseMonthly × duration) when policyAccelerationBenefitDurationMonths /
// policyEobBenefitDurationMonths are set; otherwise fall back to the legacy
// dollar inputs so older policies keep producing the v1 numbers bit-equal.
// See docs/nyl-asset-flex/option-a-derive-from-monthly.md.
function deriveAtIssuePools(ltcPolicy: PolicyFundingSource): {
  baseAtIssue: number;
  eobAtIssue: number;
} {
  const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
  const accelMonths = ltcPolicy.policyAccelerationBenefitDurationMonths;
  const eobMonths = ltcPolicy.policyEobBenefitDurationMonths;
  const baseAtIssue =
    accelMonths != null
      ? baseMonthly * accelMonths
      : ltcPolicy.policyMaximumBenefitAmount ?? 0;
  const eobAtIssue =
    eobMonths != null
      ? baseMonthly * eobMonths
      : ltcPolicy.policyContinuationBenefitAmount ?? 0;
  return { baseAtIssue, eobAtIssue };
}


export const nylAssetFlexStrategy: PolicyProjectionStrategy<
  NylAssetFlexState,
  NylAssetFlexDeltas
> = {
  initClaimState(client, ltcPolicy): MonthlyState<NylAssetFlexState> {
    const { baseAtIssue, eobAtIssue } = deriveAtIssuePools(ltcPolicy);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    // ACIO compounds every policy anniversary regardless of whether the
    // claim has started, but the projection only spans claim months — so
    // any anniversaries that fired between policy issue and claim start
    // never get a chance to accrue inside the projection loop. Pre-inflate
    // the pools by the ACIO factor at claim start so the projection picks
    // up the carrier's actual current pool, not the year-1 capacity.
    const acio = ltcPolicy.policyInflationProtection ?? 0;
    const capYears = ltcPolicy.policyInflationProtectionYears ?? 0;
    const baseRate = ltcPolicy.policyInflationProtectionOnFaceAmount ?? acio;
    const eobRate = ltcPolicy.policyInflationProtectionOnCOBAmount ?? acio;
    const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
    const baseAtClaimStart =
      baseAtIssue * compoundFactor(baseRate, capYears, claimStartPolicyYear);
    const eobAtClaimStart =
      eobAtIssue * compoundFactor(eobRate, capYears, claimStartPolicyYear);
    return {
      pools: {
        baseBenefitPool: Math.max(baseAtClaimStart - utilizedToDate, 0),
        extensionOfBenefitsPool: eobAtClaimStart,
        deathBenefit: Math.max(faceAmount, minGuaranteed),
      },
      waitingPeriod: {
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
          ltcPolicy.homeCareWaitingPeriodDays ??
          0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: {},
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const acio = ltcPolicy.policyInflationProtection ?? 0;
    const capYears = ltcPolicy.policyInflationProtectionYears ?? 0;
    const clientAsClient = client as Client;
    const projectionAnchorYear = clientAsClient.clientAddedDateTime
      ? clientAsClient.clientAddedDateTime.getFullYear()
      : new Date().getFullYear();
    const { baseAtIssue, eobAtIssue } = deriveAtIssuePools(ltcPolicy);
    return {
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Resolve every environment through the same helper the payout math uses,
      // so the displayed cap matches the math. A raw `?? 0` would collapse a
      // sparse/unpopulated env field to a hard $0 the presentation can't rescue
      // (0 isn't nullish), misrepresenting the cap; the helper honors explicit 0
      // and falls back to the max populated daily for sparse imports.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(ltcPolicy, CareEnvironment.home),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // Kept populated for legacy/UI compatibility on terms.inflationRate.
      inflationRate: acio,
      inflationCapYears: capYears,
      baseBenefitPoolInflationRate:
        ltcPolicy.policyInflationProtectionOnFaceAmount ?? acio,
      baseBenefitPoolInflationCapYears: capYears,
      extensionOfBenefitsPoolInflationRate:
        ltcPolicy.policyInflationProtectionOnCOBAmount ?? acio,
      extensionOfBenefitsPoolInflationCapYears: capYears,
      baseBenefitPoolAtIssue: baseAtIssue,
      extensionOfBenefitsPoolAtIssue: eobAtIssue,
      // Only surface the duration when the duration-derived model is active.
      // Legacy policies (dollar inputs) leave these undefined so the UI does
      // not show a misleading "× N months" derivation.
      baseBenefitPoolDurationMonths:
        ltcPolicy.policyAccelerationBenefitDurationMonths ?? undefined,
      extensionOfBenefitsPoolDurationMonths:
        ltcPolicy.policyEobBenefitDurationMonths ?? undefined,
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: true,
      isIndemnity: false,
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear, calendarYearOffset } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const acio = ltcPolicy.policyInflationProtection ?? 0;
    const capYears = ltcPolicy.policyInflationProtectionYears ?? 0;
    const baseRate = ltcPolicy.policyInflationProtectionOnFaceAmount ?? acio;
    const eobRate = ltcPolicy.policyInflationProtectionOnCOBAmount ?? acio;

    const baseFactor = compoundFactor(baseRate, capYears, policyYear);
    const eobFactor = compoundFactor(eobRate, capYears, policyYear);

    const { baseAtIssue, eobAtIssue } = deriveAtIssuePools(ltcPolicy);
    const inflatedBaseMonthly = baseMonthly * baseFactor;
    const inflatedAcceleration = baseAtIssue * baseFactor;
    const inflatedEob = eobAtIssue * eobFactor;
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;

    return {
      policyYear,
      calendarYearOffset,
      effectiveDailyBenefit: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: 0,
      effectiveFaceAmount: faceAmount,
      effectiveContinuationBenefit: inflatedEob,
      effectiveTotalLtcBenefit: inflatedAcceleration + inflatedEob,
      monthlyBenefitCap: inflatedBaseMonthly,
      theoreticalLtcPoolCeiling: inflatedAcceleration + inflatedEob,
    };
  },

  computeMonth(
    input: MonthComputeInput<NylAssetFlexState, NylAssetFlexDeltas>,
  ): MonthlyEntry<NylAssetFlexState, NylAssetFlexDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    // 1. Day classification (2-ADL bucket → WP bucket for this care
    //    environment → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      openState.waitingPeriod,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Cost metrics + per-month dollar cap (snapshot reflects this year's ACIO).
    const costMetrics = computeMonthlyCostMetrics(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );

    // Build the year-snapshot inline (processClaimProjection only caches it
    // after the first month of the year is computed). We need this row's
    // inflated cap right now.
    const acio = ltcPolicy.policyInflationProtection ?? 0;
    const capYears = ltcPolicy.policyInflationProtectionYears ?? 0;
    const baseRate = ltcPolicy.policyInflationProtectionOnFaceAmount ?? acio;
    const eobRate = ltcPolicy.policyInflationProtectionOnCOBAmount ?? acio;
    const calendarYearOffset = Math.floor(monthIndex / 12);
    const policyYear = getCalendarPolicyYear(clientAsClient, monthIndex);

    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const monthlyCap = baseMonthly * compoundFactor(baseRate, capYears, policyYear);

    // 3. Annual ACIO accrual at policy-year boundary. Accrual is computed
    //    against the AT-ISSUE pool amount (not the remaining pool), but
    //    only on a pool that's still open. Once a pool drains to $0 it
    //    stays exhausted — ACIO cannot reopen a closed pool. (Carrier
    //    model: the policy's lifetime LTC benefit caps at whatever was
    //    drawn before exhaustion, regardless of how the inflated ceiling
    //    would have continued to rise.)
    let baseInflationAccrual = 0;
    let eobInflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const priorYear = priorMonth.context.policyYear;
      const { baseAtIssue: baseInitial, eobAtIssue: eobInitial } =
        deriveAtIssuePools(ltcPolicy);
      const baseFactorDelta =
        compoundFactor(baseRate, capYears, policyYear) -
        compoundFactor(baseRate, capYears, priorYear);
      const eobFactorDelta =
        compoundFactor(eobRate, capYears, policyYear) -
        compoundFactor(eobRate, capYears, priorYear);
      if (openState.pools.baseBenefitPool > 0) {
        baseInflationAccrual = baseInitial * baseFactorDelta;
      }
      if (openState.pools.extensionOfBenefitsPool > 0) {
        eobInflationAccrual = eobInitial * eobFactorDelta;
      }
    }
    const baseAfterAccrual = openState.pools.baseBenefitPool + baseInflationAccrual;
    const eobAfterAccrual =
      openState.pools.extensionOfBenefitsPool + eobInflationAccrual;

    // 4. Reimbursement math: pay min(eligibleCost, proRatedCap).
    const proRatedCap = monthlyCap * (eligibleDays / DAYS_IN_MONTH);
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;
    const wouldPay = Math.min(eligibleCost, proRatedCap);

    // 5. Two-pool drawdown: Acceleration first, EOB second.
    const drawFromBase = Math.min(wouldPay, baseAfterAccrual);
    const drawFromEob = Math.min(wouldPay - drawFromBase, eobAfterAccrual);
    const ltcCoveragePaid = drawFromBase + drawFromEob;

    // 6. Closing balances. Only Acceleration drawdown depletes the death benefit.
    const closeBase = baseAfterAccrual - drawFromBase;
    const closeEob = eobAfterAccrual - drawFromEob;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const closeDeathBenefit = Math.max(
      openState.pools.deathBenefit - drawFromBase,
      minGuaranteed,
    );
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && drawFromBase > deathBenefitReduction;

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      calendarYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid,
      ltcPayoutToCost: ltcCoveragePaid,
      ineligibleCost: costMetrics.effectiveCareCost - ltcCoveragePaid,
      indemnityPayout: 0,
    };

    const deltas: MonthlyDeltas<NylAssetFlexDeltas> = {
      payout,
      base: { depletion: drawFromBase, inflationAccrual: baseInflationAccrual },
      eob: { depletion: drawFromEob, inflationAccrual: eobInflationAccrual },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {},
    };

    const closeState: MonthlyState<NylAssetFlexState> = {
      pools: {
        baseBenefitPool: closeBase,
        extensionOfBenefitsPool: closeEob,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: {},
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBase + closeEob <= 0,
      clientPoolExhausted: closeBase + closeEob <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<NylAssetFlexState, NylAssetFlexDeltas>({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: baseRate > 0 || eobRate > 0,
    });

    const ineligibilityReason = deriveIneligibilityReason<NylAssetFlexState>({
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    });

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason },
    };
  },

  finalizeDeathBenefit({
    closingState,
  }: FinalizeDeathBenefitInput<NylAssetFlexState>): number {
    return closingState.pools.deathBenefit;
  },
};
